import * as React from 'react';
import './Billing.scss';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import { Modal, ModalHeader, ModalBody, ListGroup, ListGroupItem } from 'reactstrap';
import StyledTable from '../../components/StyledTable';
import moment from 'moment';
import { PaymentOrigin } from '../../models';
import { SwitchField } from '../../components/Forms/fields';
import { formatEuros } from '../../shared/string';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { CSVLink } from 'react-csv';
import { TotalsGrid, StatCard, CardLabel, CardValue, CardSubValue } from './BillingStatCards';
import { getCompanyPublicDataAsync, getSystemTenantsPaymentsAsync, getSystemTenantsPaymentsMinComissionsAsync, MinComissionRate, SystemPaymentData } from '../../ajax/requests';
import Typography from '../../components/Typography';
import { PaymentLabel } from 'src/components/Forms/fields/PaymentsList';

// Module-level cache — persists for the session, company info rarely changes
const companyCache = new Map<string, Promise<{ name: string; logo: string | null }>>();

const fetchCompanyInfo = (tenantId: number) => {
    const key = String(tenantId);
    if (!companyCache.has(key)) {
        companyCache.set(key,
            getCompanyPublicDataAsync(key)
                .then(d => ({ name: d.name, logo: d.logo ?? null }))
                .catch(() => ({ name: key, logo: null }))
        );
    }
    return companyCache.get(key)!;
};

const originLabel = (origin: PaymentOrigin, t: ReturnType<typeof useTranslations>): string => {
    switch (origin) {
        case PaymentOrigin.Appointment: return t(TK.appointment);
        case PaymentOrigin.Pack: return t(TK.pack);
        case PaymentOrigin.Order: return t(TK.orders);
        default: return t(TK.unknown);
    }
};

interface CompanyInfo { name: string; logo: string | null }

interface TenantSummary {
    tenantId: number;
    revenue: number;
    commission: number;
    minCommission: number;
    count: number;
}

const VAT_RATE = 1.23;

const formatPct = (part: number, total: number): string =>
    total !== 0 ? ` (${Math.round(part / total * 100)}%)` : '';

const CompanyCell: React.FC<{ tenantId: number; companyMap: Record<number, CompanyInfo> }> = ({ tenantId, companyMap }) => {
    const company = companyMap[tenantId];
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
            {company?.logo && (
                <img src={company.logo} alt="" style={{ height: 20, width: 20, objectFit: 'contain', borderRadius: 2 }} />
            )}
            <span>{company?.name ?? tenantId}</span>
        </div>
    );
};

const SystemPayments: React.FC = () => {
    const t = useTranslations();

    const [dates, setDates] = React.useState<DateRangeSelectorState>({
        from: moment().startOf('month').format('YYYY-MM-DD'),
        to: moment().endOf('month').format('YYYY-MM-DD'),
        type: 'month',
    });

    const [payments, setPayments] = React.useState<SystemPaymentData[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [groupByTenant, setGroupByTenant] = React.useState(true);
    const [paymentsToShow, showPayments] = React.useState<SystemPaymentData[]>([]);
    const [companyMap, setCompanyMap] = React.useState<Record<number, CompanyInfo>>({});
    const [minComissions, setMinComissions] = React.useState<Record<string, MinComissionRate>>({});

    React.useEffect(() => {
        getSystemTenantsPaymentsMinComissionsAsync().then(setMinComissions);
    }, []);

    React.useEffect(() => {
        if (!dates) return;
        setIsLoading(true);
        getSystemTenantsPaymentsAsync(dates.from, dates.to)
            .then(setPayments)
            .finally(() => setIsLoading(false));
    }, [dates]);

    // Fetch company info for any tenant IDs not yet in the map
    React.useEffect(() => {
        if (!payments.length) return;
        const newIds = [...new Set(payments.map(p => p.tenantId))].filter(id => !(id in companyMap));
        if (!newIds.length) return;
        Promise.all(newIds.map(id => fetchCompanyInfo(id).then(info => ({ id, info }))))
            .then(results => setCompanyMap(prev => {
                const next = { ...prev };
                results.forEach(({ id, info }) => { next[id] = info; });
                return next;
            }));
    }, [payments]);

    const totalRevenue = React.useMemo(() =>
        payments.reduce((acc, p) => acc + p.value, 0)
    , [payments]);

    const totalCommission = React.useMemo(() =>
        payments.reduce((acc, p) => acc + (p.comissionWithoutVat ? p.comissionWithoutVat * VAT_RATE : 0), 0)
    , [payments]);

    const calcMinComission = React.useCallback((type: string, value: number): number | null => {
        const rate = minComissions[type];
        if (!rate) return null;
        return (rate.percentage * value + rate.fixed) * VAT_RATE;
    }, [minComissions]);

    const totalMinCommission = React.useMemo(() =>
        payments.reduce((acc, p) => {
            const minC = calcMinComission(p.type, p.value);
            return acc + (minC ?? 0);
        }, 0)
    , [payments, calcMinComission]);

    const totalNetCommission = totalCommission - totalMinCommission;

    const tenantSummaries = React.useMemo((): TenantSummary[] => {
        const map = payments.reduce<Record<number, TenantSummary>>((acc, p) => {
            const existing = acc[p.tenantId] ?? { tenantId: p.tenantId, revenue: 0, commission: 0, minCommission: 0, count: 0 };
            const minC = calcMinComission(p.type, p.value) ?? 0;
            return {
                ...acc,
                [p.tenantId]: {
                    ...existing,
                    revenue: existing.revenue + p.value,
                    commission: existing.commission + (p.comissionWithoutVat ? p.comissionWithoutVat * VAT_RATE : 0),
                    minCommission: existing.minCommission + minC,
                    count: existing.count + 1,
                },
            };
        }, {});
        return Object.values(map).sort((a, b) => b.revenue - a.revenue);
    }, [payments, calcMinComission]);

    const csvData = React.useMemo(() =>
        payments.map(p => {
            const actual = p.comissionWithoutVat ? p.comissionWithoutVat * VAT_RATE : null;
            const minC = calcMinComission(p.type, p.value);
            const net = actual !== null && minC !== null ? actual - minC : null;
            return {
                tenantId: p.tenantId,
                company: companyMap[p.tenantId]?.name ?? p.tenantId,
                date: p.date.substring(0, 10),
                client: p.clientName,
                origin: originLabel(p.origin, t),
                type: p.type,
                value: p.value,
                commission: actual !== null ? actual.toFixed(2) : '',
                minCommission: minC !== null ? minC.toFixed(2) : '',
                netCommission: net !== null ? net.toFixed(2) : '',
            };
        })
    , [payments, companyMap, calcMinComission]);

    return (
        <div className='billingContainer'>
            {isLoading && <Typography style={{ position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white' }}>{t(TK.loading)}...</Typography>}

            <DateRangeSelector onRangeChanged={setDates} />

            {isLoading ? (
                <Typography>{t(TK.loading)}...</Typography>
            ) : (
                <>
                    <TotalsGrid style={{ marginTop: 'var(--spacer-S)' }}>
                        <StatCard accent="var(--success-color)">
                            <CardLabel>{t(TK.income)}</CardLabel>
                            <CardValue textColor="var(--success-color)">{formatEuros(totalRevenue)}</CardValue>
                            <CardSubValue>{payments.length} {t(TK.payments).toLowerCase()}</CardSubValue>
                        </StatCard>
                        <StatCard accent="var(--info-color)">
                            <CardLabel>{t(TK.commissions)}</CardLabel>
                            <CardValue textColor="var(--info-color)">{formatEuros(totalCommission)}</CardValue>
                            <CardSubValue>{tenantSummaries.length} tenants</CardSubValue>
                        </StatCard>
                        <StatCard accent="var(--text-muted)">
                            <CardLabel>{t(TK.minComission)}</CardLabel>
                            <CardValue textColor="var(--text-muted)">{formatEuros(totalMinCommission)}</CardValue>
                        </StatCard>
                        <StatCard accent={totalNetCommission >= 0 ? 'var(--success-color)' : 'var(--danger-color)'}>
                            <CardLabel>{t(TK.netComission)}</CardLabel>
                            <CardValue textColor={totalNetCommission >= 0 ? 'var(--success-color)' : 'var(--danger-color)'}>{formatEuros(totalNetCommission)}</CardValue>
                            <CardSubValue>{formatPct(totalNetCommission, totalCommission)}</CardSubValue>
                        </StatCard>
                    </TotalsGrid>

                    <div style={{ display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', marginTop: 'var(--spacer-S)' }}>
                        <SwitchField
                            style={{ display: 'inline' }}
                            label={t(TK.dontGroup)}
                            value={!groupByTenant}
                            id='system-dont-group'
                            onChange={v => setGroupByTenant(!v)}
                        />
                    </div>

                    {groupByTenant ? (
                        <div className="tableContainer" style={{ marginTop: 'var(--spacer-S)' }}>
                            <StyledTable hover>
                                <thead>
                                    <tr>
                                        <th>{t(TK.company)}</th>
                                        <th>{t(TK.payments)}</th>
                                        <th>{t(TK.income)}</th>
                                        <th>{t(TK.commissions)}</th>
                                        <th>{t(TK.minComission)}</th>
                                        <th>{t(TK.netComission)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenantSummaries.map(s => {
                                        const net = s.commission - s.minCommission;
                                        return (
                                            <tr
                                                key={s.tenantId}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => showPayments(payments.filter(p => p.tenantId === s.tenantId))}
                                            >
                                                <td><CompanyCell tenantId={s.tenantId} companyMap={companyMap} /></td>
                                                <td>{s.count}</td>
                                                <td style={{ color: 'var(--success-color)', fontWeight: 600 }}>{formatEuros(s.revenue)}</td>
                                                <td style={{ color: 'var(--info-color)', fontWeight: 600 }}>{formatEuros(s.commission)}</td>
                                                <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{formatEuros(s.minCommission)}</td>
                                                <td style={{ color: net >= 0 ? 'var(--success-color)' : 'var(--danger-color)', fontWeight: 600 }}>{formatEuros(net)}{formatPct(net, s.commission)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </StyledTable>
                        </div>
                    ) : (
                        <div className="tableContainer" style={{ marginTop: 'var(--spacer-S)' }}>
                            <StyledTable scrollable hover>
                                <thead>
                                    <tr>
                                        <th>{t(TK.date)}</th>
                                        <th>{t(TK.company)}</th>
                                        <th>{t(TK.client)}</th>
                                        <th>{t(TK.origin)}</th>
                                        <th>{t(TK.type)}</th>
                                        <th>{t(TK.value)}</th>
                                        <th>{t(TK.commissions)}</th>
                                        <th>{t(TK.minComission)}</th>
                                        <th>{t(TK.netComission)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map((p, i) => {
                                        const actual = p.comissionWithoutVat ? p.comissionWithoutVat * VAT_RATE : null;
                                        const minC = calcMinComission(p.type, p.value);
                                        const net = actual !== null && minC !== null ? actual - minC : null;
                                        return (
                                            <tr key={`${p.id}-${i}`}>
                                                <td>{p.date.substring(0, 10)}</td>
                                                <td><CompanyCell tenantId={p.tenantId} companyMap={companyMap} /></td>
                                                <td>{p.clientName}</td>
                                                <td>{originLabel(p.origin, t)}</td>
                                                <td><PaymentLabel paymentType={p.type} /></td>
                                                <td style={{ color: 'var(--success-color)', fontWeight: 600 }}>{formatEuros(p.value)}</td>
                                                <td style={{ color: 'var(--info-color)', fontWeight: 600 }}>
                                                    {actual !== null ? formatEuros(actual) : '—'}
                                                </td>
                                                <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                                                    {minC !== null ? formatEuros(minC) : '—'}
                                                </td>
                                                <td style={{ color: net !== null && net >= 0 ? 'var(--success-color)' : 'var(--danger-color)', fontWeight: 600 }}>
                                                    {net !== null ? `${formatEuros(net)}${formatPct(net, actual!)}` : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </StyledTable>
                        </div>
                    )}

                    <CSVLink
                        data={csvData}
                        separator=';'
                        target='_blank'
                        filename='system-commissions-export.csv'
                    >
                        {t(TK.exportData)}
                    </CSVLink>
                </>
            )}

            <Modal size='lg' isOpen={!!paymentsToShow.length} toggle={() => showPayments([])}>
                <ModalHeader toggle={() => showPayments([])}>{t(TK.payments)}</ModalHeader>
                <ModalBody>
                    <ListGroup>
                        {paymentsToShow.map((p, i) => {
                            const actual = p.comissionWithoutVat ? p.comissionWithoutVat * VAT_RATE : null;
                            const minC = calcMinComission(p.type, p.value);
                            const net = actual !== null && minC !== null ? actual - minC : null;
                            return (
                                <ListGroupItem key={`${p.id}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div><b>{p.clientName}</b> ({originLabel(p.origin, t)})</div>
                                        <div><PaymentLabel paymentType={p.type} /> — {p.date.substring(0, 10)}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <b>{formatEuros(p.value)}</b>
                                        {actual !== null && (
                                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                {t(TK.comission)}: {formatEuros(actual)}
                                            </div>
                                        )}
                                        {minC !== null && (
                                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                {t(TK.minComission)}: {formatEuros(minC)}
                                            </div>
                                        )}
                                        {net !== null && (
                                            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: net >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                                                {t(TK.netComission)}: {formatEuros(net)}{formatPct(net, actual!)}
                                            </div>
                                        )}
                                    </div>
                                </ListGroupItem>
                            );
                        })}
                    </ListGroup>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default SystemPayments;
